/**
 * Admin → Workflow Designer (Session 2).
 *
 * Visual editor for a WorkflowDefinition. Renders Stages as columns and
 * Steps as cards. Click a step to open the side drawer for in-place
 * editing of triggers, actions, and conditions.
 *
 * Only DRAFT definitions are editable. From a LIVE definition, the UI
 * surfaces a "Clone for edit" button that calls /clone and redirects to
 * the resulting draft.
 *
 * Route: /admin/workflow-engine/designer/:definitionId
 *
 * The React Flow / pan-zoom canvas variant is deferred to S3; this
 * column layout gives admins immediate access to every editable field.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import SandboxLaunchModal from "./SandboxLaunchModal";
import AssertionsEditor, { formatExpected } from "./AssertionsEditor";
import {
  ArrowLeft, ArrowDown, ArrowUp, Beaker, CheckCircle2, GitBranch, Pencil, Plus, Save, Sparkles, Trash2, Truck, X, XCircle,
} from "lucide-react";

const STATE_PALETTE = {
  draft: "bg-slate-100 text-slate-700",
  testing: "bg-violet-100 text-violet-700",
  scheduled: "bg-amber-100 text-amber-800",
  live: "bg-emerald-100 text-emerald-700",
  paused: "bg-yellow-100 text-yellow-800",
  archived: "bg-slate-100 text-slate-500",
};

// Canonical event registry — loaded once from /admin/wfe/events/registry.
// Falls back to a hard-coded list of the 12 canonical types so the drawer
// still renders sensibly if the request fails.
const FALLBACK_EVENT_TYPES = [
  { type: "patient_enrolled",          category: "lifecycle" },
  { type: "intake_completed",          category: "lifecycle" },
  { type: "appointment_completed",     category: "clinical" },
  { type: "assessment_completed",      category: "clinical" },
  { type: "lab_ordered",               category: "vendor" },
  { type: "lab_received",              category: "vendor" },
  { type: "provider_review_completed", category: "clinical" },
  { type: "vendor_completed",          category: "vendor" },
  { type: "communication_sent",        category: "communications" },
  { type: "communication_opened",      category: "communications" },
  { type: "membership_started",        category: "membership" },
  { type: "membership_renewed",        category: "membership" },
];

// Common context keys admins pull from in condition expressions. Feeds the
// datalist suggestions on the condition builder's variable input.
const COMMON_CONTEXT_KEYS = [
  "context.score",
  "context.tier",
  "context.risk_level",
  "context.lab_result",
  "context.follow_up_required",
  "context.patient_type",
  "context.consent_given",
  "context.days_since_intake",
  "context.membership_active",
];


// 'vendor_dispatch' gets a dedicated vendor/service picker below; the others
// just store their config as-is. Power users can still set any custom type.
const KNOWN_ACTION_TYPES = [
  { value: "notify",          label: "Notify (email / SMS)" },
  { value: "task",            label: "Create task" },
  { value: "webhook",         label: "Call webhook" },
  { value: "wait",            label: "Wait" },
  { value: "vendor_dispatch", label: "Dispatch to vendor" },
];

const COMPARISON_OPS = ["==", "!=", ">", "<", ">=", "<="];

function StatePill({ value }) {
  const cls = STATE_PALETTE[value] || "bg-slate-100 text-slate-700";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>
      {value}
    </span>
  );
}

function newId(prefix) {
  return `${prefix}${Math.random().toString(16).slice(2, 14)}`;
}

/**
 * ConditionRow renders a single condition. Each condition has:
 *   expression: {op: [{var: "field"}, literal]}
 *   if_true_next:  step.key or step.id of the truthy branch
 *   if_false_next: step.key or step.id of the falsy branch
 *
 * This editor renders the common 'binary comparison' shape inline. More
 * complex AND/OR/NOT nesting is supported by the backend evaluator but
 * not by this UI in v1 — power users edit the raw JSON.
 */
function ConditionRow({ condition, allStepKeys, onChange, onRemove }) {
  const expr = condition.expression || {};
  const [op] = Object.keys(expr).length === 1 ? Object.keys(expr) : ["=="];
  const args = expr[op] || [{ var: "" }, ""];
  const varName = (args[0] && args[0].var) || "";
  const literal = args[1] ?? "";
  const isComparison = COMPARISON_OPS.includes(op);
  const datalistId = `cond-vars-${condition.id}`;

  const setExpr = (nextOp, nextVar, nextLit) => {
    onChange({
      ...condition,
      expression: { [nextOp]: [{ var: nextVar }, nextLit] },
    });
  };

  return (
    <div
      data-testid={`condition-row-${condition.id}`}
      className="rounded-lg border border-slate-800 bg-slate-900/60 p-2 space-y-2"
    >
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-bold text-amber-300">
        <span className="inline-flex items-center gap-1"><GitBranch className="w-3 h-3" /> Branch</span>
        <button
          type="button"
          onClick={onRemove}
          className="text-slate-500 hover:text-rose-400"
          data-testid={`condition-remove-${condition.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      {isComparison ? (
        <>
          <datalist id={datalistId}>
            {COMMON_CONTEXT_KEYS.map((k) => <option key={k} value={k} />)}
          </datalist>
          <div className="flex items-center gap-1.5 text-xs flex-wrap">
            <span className="text-slate-400 font-bold">IF</span>
            <input
              value={varName}
              list={datalistId}
              onChange={(e) => setExpr(op, e.target.value, literal)}
              placeholder="context.score"
              className="flex-1 min-w-[120px] bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[11px] font-mono"
            />
            <select
              value={op}
              onChange={(e) => setExpr(e.target.value, varName, literal)}
              className="bg-slate-900 border border-slate-700 rounded px-1.5 py-1 text-[11px] font-mono"
              data-testid={`condition-op-${condition.id}`}
            >
              {COMPARISON_OPS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <input
              value={literal}
              onChange={(e) => {
                const v = e.target.value;
                const num = Number(v);
                setExpr(op, varName, v !== "" && !Number.isNaN(num) ? num : v);
              }}
              placeholder="value"
              className="w-24 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[11px] font-mono"
            />
          </div>
        </>
      ) : (
        <div className="text-[10px] text-amber-300 italic">
          Complex expression — edit JSON via API in v1.
        </div>
      )}
      <div className="flex items-center gap-1.5 text-xs">
        <span className="text-emerald-400 font-bold w-14">THEN →</span>
        <select
          value={condition.if_true_next || ""}
          onChange={(e) => onChange({ ...condition, if_true_next: e.target.value })}
          className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[11px]"
        >
          <option value="">(next step in sequence)</option>
          {allStepKeys.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-1.5 text-xs">
        <span className="text-rose-400 font-bold w-14">ELSE →</span>
        <select
          value={condition.if_false_next || ""}
          onChange={(e) => onChange({ ...condition, if_false_next: e.target.value })}
          className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[11px]"
        >
          <option value="">(next step in sequence)</option>
          {allStepKeys.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
    </div>
  );
}

/**
 * TriggerRow — dropdown of canonical event types (grouped by category)
 * plus a "Custom event…" escape hatch. Beats free-text typing that led to
 * subtle typo bugs like `patient_enroled`.
 */
function TriggerRow({ trigger, eventTypes, onChange, onRemove }) {
  const currentType = trigger.event_type || "";
  const known = eventTypes.some((e) => e.type === currentType);
  const grouped = eventTypes.reduce((acc, e) => {
    (acc[e.category] = acc[e.category] || []).push(e);
    return acc;
  }, {});

  return (
    <div
      data-testid={`trigger-row-${trigger.id}`}
      className="flex items-center gap-1.5"
    >
      <select
        data-testid={`trigger-type-${trigger.id}`}
        value={known ? currentType : (currentType ? "__custom__" : "")}
        onChange={(e) => {
          const next = e.target.value;
          if (next === "__custom__") onChange({ ...trigger, event_type: currentType && !known ? currentType : "" });
          else onChange({ ...trigger, event_type: next });
        }}
        className="flex-1 min-w-0 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[11px] font-mono"
      >
        <option value="">— Pick an event —</option>
        {Object.entries(grouped).sort().map(([cat, evts]) => (
          <optgroup key={cat} label={cat}>
            {evts.map((e) => <option key={e.type} value={e.type}>{e.type}</option>)}
          </optgroup>
        ))}
        <option value="__custom__">Custom event…</option>
      </select>
      {(!known && currentType) && (
        <input
          value={currentType}
          onChange={(e) => onChange({ ...trigger, event_type: e.target.value })}
          placeholder="custom_event_type"
          className="w-40 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[11px] font-mono"
        />
      )}
      <button
        type="button"
        className="text-slate-500 hover:text-rose-400"
        onClick={onRemove}
        data-testid={`trigger-remove-${trigger.id}`}
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}


/**
 * ActionRow renders a single step Action.
 *
 *   action = { id, type, config:{}, blocking }
 *
 * Behaviour:
 *   - Type is a dropdown of the known set plus a "Custom…" option that lets
 *     power users keep typing any custom string.
 *   - When type is `vendor_dispatch`, a vendor + service picker is rendered
 *     below the type selector. The picked vendor service is written to
 *     `config = { vendor_id, vendor_name, service_id, service_name, custom_link, custom_link_reason }`.
 *   - Otherwise the row stays compact — config is preserved untouched on
 *     save so existing JSON-edited actions don't get clobbered.
 */
function ActionRow({ action, vendorServices, onChange, onRemove }) {
  const type = action.type || "";
  const isKnown = KNOWN_ACTION_TYPES.some((k) => k.value === type);

  const update = (patch) => onChange({ ...action, ...patch });
  const updateConfig = (patch) => update({ config: { ...(action.config || {}), ...patch } });

  return (
    <div
      data-testid={`action-row-${action.id}`}
      className="rounded-lg border border-slate-800 bg-slate-900/60 p-2 space-y-2"
    >
      <div className="flex items-center gap-1.5 text-xs">
        <select
          data-testid={`action-type-${action.id}`}
          value={isKnown ? type : "__custom__"}
          onChange={(e) => {
            const next = e.target.value;
            if (next === "__custom__") {
              update({ type: type && !isKnown ? type : "" });
            } else if (next !== "vendor_dispatch") {
              // Drop vendor-specific config when leaving vendor_dispatch.
              const { vendor_id, vendor_name, service_id, service_name, custom_link, custom_link_reason, ...rest } = action.config || {};
              update({ type: next, config: rest });
            } else {
              update({ type: next });
            }
          }}
          className="bg-slate-900 border border-slate-700 rounded px-1.5 py-1 text-[11px] font-mono flex-1 min-w-0"
        >
          {KNOWN_ACTION_TYPES.map((k) => (
            <option key={k.value} value={k.value}>{k.label}</option>
          ))}
          <option value="__custom__">Custom…</option>
        </select>
        <button
          type="button"
          className="text-slate-500 hover:text-rose-400"
          onClick={onRemove}
          data-testid={`action-remove-${action.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {/* Custom type — free text input */}
      {!isKnown && (
        <input
          value={type}
          onChange={(e) => update({ type: e.target.value })}
          placeholder="custom_action_type"
          className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[11px] font-mono"
        />
      )}

      {/* Vendor dispatch picker */}
      {type === "vendor_dispatch" && (
        <VendorDispatchPicker
          action={action}
          vendorServices={vendorServices}
          onChangeConfig={updateConfig}
        />
      )}

      {/* Blocking toggle */}
      <label className="flex items-center gap-1.5 text-[10px] text-slate-400 cursor-pointer">
        <input
          type="checkbox"
          checked={action.blocking !== false}
          onChange={(e) => update({ blocking: e.target.checked })}
          className="accent-blue-500"
        />
        Block run until this action completes
      </label>
    </div>
  );
}

/**
 * VendorDispatchPicker — vendor + service double-select.
 *
 * Vendor list is derived from the approved vendor_services rows. Picking a
 * vendor narrows the service list. Picking a service writes the full
 * denormalised config blob so the engine + audit log can render rich event
 * details without re-resolving FKs.
 */
function VendorDispatchPicker({ action, vendorServices, onChangeConfig }) {
  const config = action.config || {};
  const approved = (vendorServices || []).filter((s) => s.status === "approved");

  // Group services by vendor.
  const vendors = useMemo(() => {
    const map = new Map();
    approved.forEach((s) => {
      if (!s.vendor_id) return;
      if (!map.has(s.vendor_id)) {
        map.set(s.vendor_id, { vendor_id: s.vendor_id, vendor_name: s.vendor_name || "—", services: [] });
      }
      map.get(s.vendor_id).services.push(s);
    });
    return Array.from(map.values()).sort((a, b) => (a.vendor_name || "").localeCompare(b.vendor_name || ""));
  }, [approved]);

  const selectedVendor = vendors.find((v) => v.vendor_id === config.vendor_id);
  const servicesForVendor = selectedVendor?.services || [];

  return (
    <div
      data-testid={`vendor-dispatch-picker-${action.id}`}
      className="rounded-md border border-blue-900/40 bg-blue-950/40 p-2 space-y-1.5"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-blue-300">
        <Truck className="w-3 h-3" /> Vendor dispatch
      </div>

      {vendors.length === 0 ? (
        <div className="text-[10px] text-amber-300 italic">
          No approved vendor services found. Approve a vendor service first under <strong>Admin → Vendor Services</strong>.
        </div>
      ) : (
        <>
          <div>
            <label className="text-[10px] text-slate-400">Vendor</label>
            <select
              data-testid={`vendor-dispatch-vendor-${action.id}`}
              value={config.vendor_id || ""}
              onChange={(e) => {
                const v = vendors.find((vv) => vv.vendor_id === e.target.value);
                onChangeConfig({
                  vendor_id: v?.vendor_id || "",
                  vendor_name: v?.vendor_name || "",
                  // Clear the previously-selected service when vendor changes.
                  service_id: "",
                  service_name: "",
                  custom_link: null,
                  custom_link_reason: null,
                });
              }}
              className="mt-0.5 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[11px]"
            >
              <option value="">— Select vendor —</option>
              {vendors.map((v) => (
                <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>
              ))}
            </select>
          </div>

          {config.vendor_id && (
            <div>
              <label className="text-[10px] text-slate-400">Service</label>
              <select
                data-testid={`vendor-dispatch-service-${action.id}`}
                value={config.service_id || ""}
                onChange={(e) => {
                  const svc = servicesForVendor.find((s) => s.id === e.target.value);
                  onChangeConfig({
                    service_id: svc?.id || "",
                    service_name: svc?.name || "",
                    custom_link: svc?.custom_link || null,
                    custom_link_reason: svc?.custom_link_reason || null,
                  });
                }}
                className="mt-0.5 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[11px]"
              >
                <option value="">— Select service —</option>
                {servicesForVendor.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.price ? ` · $${s.price}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Summary chip when fully configured */}
          {config.vendor_id && config.service_id && (
            <div
              data-testid={`vendor-dispatch-summary-${action.id}`}
              className="text-[10px] text-emerald-300 mt-1"
            >
              ✓ Dispatches <strong>{config.service_name}</strong> to <strong>{config.vendor_name}</strong>
              {config.custom_link && (
                <div className="text-[9px] text-slate-400 mt-0.5 break-all">
                  external link: {config.custom_link}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}


function StepDrawer({ step, allStepKeys, vendorServices, eventTypes, onChange, onClose }) {
  if (!step) return null;
  const update = (patch) => onChange({ ...step, ...patch });

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        data-testid="step-drawer"
        onClick={(e) => e.stopPropagation()}
        className="relative h-full w-[480px] bg-slate-950 border-l border-slate-800 p-5 overflow-y-auto text-slate-200"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Edit step</div>
            <div className="text-base font-bold text-slate-100">{step.name || step.key}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200" type="button" data-testid="step-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Name</label>
            <input
              value={step.name || ""}
              onChange={(e) => update({ name: e.target.value })}
              data-testid="step-name"
              className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Key (slug)</label>
            <input
              value={step.key || ""}
              onChange={(e) => update({ key: e.target.value })}
              data-testid="step-key"
              className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs font-mono"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Expected days (SLA)</label>
            <input
              type="number"
              value={step.expected_days || ""}
              onChange={(e) => update({ expected_days: e.target.value === "" ? null : Number(e.target.value) })}
              data-testid="step-expected-days"
              className="mt-1 w-32 bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs"
            />
          </div>

          {/* Triggers */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Triggers (inbound events)</label>
            <div className="mt-1 space-y-1.5">
              {(step.triggers || []).map((t, i) => (
                <TriggerRow
                  key={t.id || i}
                  trigger={t}
                  eventTypes={eventTypes}
                  onChange={(next) => {
                    const list = [...(step.triggers || [])];
                    list[i] = next;
                    update({ triggers: list });
                  }}
                  onRemove={() => update({ triggers: step.triggers.filter((_, j) => j !== i) })}
                />
              ))}
              <button
                type="button"
                onClick={() => update({ triggers: [...(step.triggers || []), { id: newId("trg_"), event_type: "" }] })}
                data-testid="step-add-trigger"
                className="text-[11px] text-blue-400 hover:underline inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add trigger
              </button>
            </div>
          </div>

          {/* Actions */}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Actions</label>
            <div className="mt-1 space-y-1.5">
              {(step.actions || []).map((a, i) => (
                <ActionRow
                  key={a.id || i}
                  action={a}
                  vendorServices={vendorServices}
                  onChange={(next) => {
                    const list = [...(step.actions || [])];
                    list[i] = next;
                    update({ actions: list });
                  }}
                  onRemove={() => update({ actions: step.actions.filter((_, j) => j !== i) })}
                />
              ))}
              <button
                type="button"
                onClick={() => update({ actions: [...(step.actions || []), { id: newId("act_"), type: "notify", config: {}, blocking: true }] })}
                data-testid="step-add-action"
                className="text-[11px] text-blue-400 hover:underline inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add action
              </button>
            </div>
          </div>

          {/* Conditions */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1">
                <GitBranch className="w-3 h-3" /> Conditions (branching)
              </label>
              <button
                type="button"
                onClick={() => update({
                  conditions: [...(step.conditions || []), {
                    id: newId("cnd_"),
                    expression: { "==": [{ var: "" }, ""] },
                    if_true_next: "", if_false_next: "",
                  }],
                })}
                data-testid="step-add-condition"
                className="text-[11px] text-blue-400 hover:underline inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add condition
              </button>
            </div>
            <div className="mt-1 space-y-2">
              {(step.conditions || []).map((c, i) => (
                <ConditionRow
                  key={c.id || i}
                  condition={c}
                  allStepKeys={allStepKeys}
                  onChange={(next) => {
                    const list = [...(step.conditions || [])];
                    list[i] = next;
                    update({ conditions: list });
                  }}
                  onRemove={() => update({ conditions: step.conditions.filter((_, j) => j !== i) })}
                />
              ))}
              {(step.conditions || []).length === 0 && (
                <div className="text-[10px] text-slate-500 italic">
                  No conditions — runs auto-advance in declared order.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditAssertionsModal({ open, scenario, onClose, onSaved }) {
  const [assertions, setAssertions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && scenario) {
      setAssertions(Array.isArray(scenario.assertions) ? scenario.assertions : []);
      setSaving(false);
    }
  }, [open, scenario]);

  if (!open || !scenario) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const r = await api.patch(`/admin/wfe/scenarios/${scenario.id}`, { assertions });
      toast.success("Assertions saved.");
      onSaved?.(r.data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-assertions-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="relative w-full max-w-lg rounded-2xl bg-white border border-slate-200 shadow-2xl p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-violet-600">
              <Pencil className="w-3 h-3" /> Edit assertions
            </div>
            <h2 className="mt-1 font-display text-lg font-bold text-slate-900">{scenario.name}</h2>
            <p className="mt-1 text-xs text-slate-500">
              Assertions are evaluated after every event replays. Failed assertions surface in the panel below.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
        <AssertionsEditor
          assertions={assertions}
          onChange={setAssertions}
          testIdPrefix="edit-scenario-assertion"
        />
        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="text-sm font-semibold text-slate-700 px-3 py-2 rounded-full hover:bg-slate-100">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="edit-scenario-assertions-submit"
            className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save assertions"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * ScenariosPanel — list + replay scenarios for the current workflow slug.
 *
 * Small collapsible section that surfaces every scenario recorded for
 * the workflow family and lets admins one-click replay against the
 * definition currently open in the designer (so admins can validate
 * their in-progress draft against a canonical regression case).
 */
function ScenariosPanel({ workflowSlug, definitionId, definitionName }) {
  const [scenarios, setScenarios] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState({}); // scenarioId -> replay result
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/wfe/scenarios", { params: { workflow_slug: workflowSlug } });
      setScenarios(r.data?.scenarios || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [workflowSlug]);

  useEffect(() => {
    if (workflowSlug) load();
  }, [workflowSlug, load]);

  const replay = async (scn) => {
    setBusy(scn.id);
    try {
      const r = await api.post(`/admin/wfe/scenarios/${scn.id}/replay`, {
        definition_id: definitionId,
      });
      setResults((prev) => ({ ...prev, [scn.id]: r.data }));
      const hasAssertions = (scn.assertions || []).length > 0;
      const fired = r.data?.dispatched?.length || 0;
      if (hasAssertions) {
        if (r.data?.passed) {
          toast.success(`✅ "${scn.name}" passed · ${fired} events, ${(scn.assertions || []).length} assertions`);
        } else {
          toast.error(`❌ "${scn.name}" failed · ${(r.data?.failed_assertions || []).length} assertion(s) failed`);
        }
      } else {
        toast.success(`Replayed "${scn.name}" · ${fired} events fired`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const openInSandbox = (runId) => navigate(`/admin/workflow-engine/sandbox/${runId}`);

  const remove = async (scn) => {
    if (!window.confirm(`Delete scenario "${scn.name}"?`)) return;
    setBusy(scn.id);
    try {
      await api.delete(`/admin/wfe/scenarios/${scn.id}`);
      toast.success("Scenario deleted");
      setResults((prev) => {
        const next = { ...prev };
        delete next[scn.id];
        return next;
      });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const handleAssertionsSaved = (updated) => {
    setScenarios((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  if (loading) return null;
  if (scenarios.length === 0) return null;

  return (
    <div className="mb-4 rounded-2xl border border-violet-200 bg-violet-50/60 p-3" data-testid="designer-scenarios-panel">
      <div className="flex items-center gap-2 mb-2">
        <Beaker className="w-4 h-4 text-violet-600" />
        <h3 className="text-[11px] uppercase tracking-wider font-bold text-violet-800">
          Recorded scenarios · replay against {definitionName}
        </h3>
      </div>
      <div className="flex flex-col gap-2">
        {scenarios.map((scn) => {
          const result = results[scn.id];
          const assertionCount = (scn.assertions || []).length;
          const passed = result?.passed;
          const badgeClass = result
            ? (passed ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                      : "bg-rose-100 text-rose-700 border-rose-200")
            : "bg-slate-100 text-slate-600 border-slate-200";
          return (
            <div
              key={scn.id}
              data-testid={`designer-scenario-${scn.id}`}
              className="rounded-lg bg-white border border-violet-200 p-2"
            >
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-slate-900 truncate max-w-[220px]">{scn.name}</span>
                    {result && (
                      <span
                        data-testid={`designer-scenario-result-${scn.id}`}
                        className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${badgeClass}`}
                      >
                        {passed ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {passed ? "Passed" : "Failed"}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {scn.events.length} events · {assertionCount} assertion{assertionCount === 1 ? "" : "s"}
                    {result?.run_id && (
                      <>
                        {" · "}
                        <button
                          type="button"
                          className="underline hover:text-violet-700"
                          onClick={() => openInSandbox(result.run_id)}
                          data-testid={`designer-scenario-open-run-${scn.id}`}
                        >
                          open replay run
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(scn)}
                  disabled={busy === scn.id}
                  data-testid={`designer-scenario-edit-${scn.id}`}
                  className="inline-flex items-center gap-1 border border-violet-300 text-violet-700 hover:bg-violet-100 rounded-full px-2 py-1 text-[11px] font-semibold disabled:opacity-40"
                  title="Edit assertions"
                >
                  <Pencil className="w-3 h-3" /> Assertions
                </button>
                <button
                  type="button"
                  onClick={() => replay(scn)}
                  disabled={busy === scn.id}
                  data-testid={`designer-scenario-replay-${scn.id}`}
                  className="inline-flex items-center gap-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full px-2 py-1 text-[11px] font-semibold disabled:opacity-40"
                >
                  <Sparkles className="w-3 h-3" /> Replay
                </button>
                <button
                  type="button"
                  onClick={() => remove(scn)}
                  disabled={busy === scn.id}
                  className="text-slate-400 hover:text-rose-500"
                  data-testid={`designer-scenario-delete-${scn.id}`}
                  title="Delete scenario"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {result?.assertion_results?.length > 0 && (
                <div
                  data-testid={`designer-scenario-assertion-results-${scn.id}`}
                  className="mt-2 border-t border-violet-100 pt-2 space-y-1"
                >
                  {result.assertion_results.map((ar, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-1.5 text-[10px] font-mono ${
                        ar.passed ? "text-emerald-700" : "text-rose-700"
                      }`}
                    >
                      {ar.passed ? (
                        <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-3 h-3 flex-shrink-0" />
                      )}
                      <span className="truncate">
                        {ar.type}
                        {ar.key ? `[${ar.key}]` : ""} = {formatExpected(ar.expected)}
                        {!ar.passed && !ar.error && ` · got ${formatExpected(ar.actual)}`}
                        {ar.error ? ` · ${ar.error}` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <EditAssertionsModal
        open={!!editing}
        scenario={editing}
        onClose={() => setEditing(null)}
        onSaved={handleAssertionsSaved}
      />
    </div>
  );
}

export default function AdminWorkflowDesigner() {
  const { definitionId } = useParams();
  const navigate = useNavigate();
  const [defn, setDefn] = useState(null);
  const [editing, setEditing] = useState(null); // {stageIndex, stepIndex}
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [vendorServices, setVendorServices] = useState([]);
  const [eventTypes, setEventTypes] = useState(FALLBACK_EVENT_TYPES);
  const [sandboxOpen, setSandboxOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/admin/wfe/definitions/${definitionId}`);
      setDefn(r.data);
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Load the vendor-service catalog + canonical event registry once. Used
  // by the step drawer's vendor picker and trigger dropdown respectively.
  useEffect(() => {
    (async () => {
      try {
        const [v, e] = await Promise.all([
          api.get("/admin/vendor-services"),
          api.get("/admin/wfe/events/registry"),
        ]);
        setVendorServices(Array.isArray(v.data) ? v.data : []);
        setEventTypes(Array.isArray(e.data?.events) ? e.data.events : FALLBACK_EVENT_TYPES);
      } catch {
        // Non-fatal — pickers fall back to sensible defaults.
        setVendorServices([]);
      }
    })();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitionId]);

  const allStepKeys = useMemo(() => {
    const out = [];
    (defn?.stages || []).forEach((s) => (s.steps || []).forEach((stp) => stp.key && out.push(stp.key)));
    return out;
  }, [defn]);

  if (!defn) {
    return (
      <ToolPageShell eyebrow="Admin · Workflow Engine" title="Designer">
        <div className="text-sm text-slate-500">Loading…</div>
      </ToolPageShell>
    );
  }

  const editable = defn.state === "draft";

  const mutate = (mutator) => {
    setDefn((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      mutator(next);
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/wfe/definitions/${definitionId}`, {
        name: defn.name, description: defn.description, stages: defn.stages,
      });
      setDefn(r.data);
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const cloneForEdit = async () => {
    try {
      const r = await api.post(`/admin/wfe/definitions/${definitionId}/clone`);
      toast.success(`Cloned to v${r.data.version} draft`);
      navigate(`/admin/workflow-engine/designer/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deploy = async () => {
    try {
      await api.post(`/admin/wfe/definitions/${definitionId}/deploy`);
      toast.success("Deployed — now live");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const editingStep = editing ? defn.stages[editing.stageIndex]?.steps?.[editing.stepIndex] : null;

  return (
    <ToolPageShell
      eyebrow={`Admin · Workflow Engine · ${defn.slug} v${defn.version}`}
      title={defn.name}
      actions={
        <div className="flex items-center gap-2">
          <StatePill value={defn.state} />
          <Link
            to="/admin/workflow-engine"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 rounded-full px-3 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
          <button
            type="button"
            onClick={() => setSandboxOpen(true)}
            disabled={dirty || defn.state === "archived" || (defn.stages || []).length === 0}
            data-testid="designer-test-run"
            className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-full px-3 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            title={dirty ? "Save before test-running" : "Start a sandbox run against this workflow"}
          >
            <Beaker className="w-3.5 h-3.5" /> Test run
          </button>
          {editable ? (
            <>
              <button
                type="button"
                onClick={save}
                disabled={!dirty || saving}
                data-testid="designer-save"
                className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={deploy}
                disabled={dirty}
                data-testid="designer-deploy"
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                title={dirty ? "Save before deploying" : "Deploy this draft to live"}
              >
                <Sparkles className="w-3.5 h-3.5" /> Deploy
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={cloneForEdit}
              data-testid="designer-clone"
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 py-2 text-sm font-semibold"
            >
              Clone for edit
            </button>
          )}
        </div>
      }
      intro={`${defn.description || "No description."}  ·  ${defn.stages?.length || 0} stages`}
    >
      {!editable && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-amber-900 text-xs">
          This definition is <strong>{defn.state}</strong> and read-only.
          Use <em>Clone for edit</em> to create a new draft version you can modify.
        </div>
      )}

      <ScenariosPanel workflowSlug={defn.slug} definitionId={defn.id} definitionName={defn.name} />

      {/* Stages column board */}
      <div className="flex gap-3 overflow-x-auto pb-3" data-testid="designer-board">
        {(defn.stages || []).map((stage, si) => (
          <div
            key={stage.id}
            data-testid={`designer-stage-${stage.id}`}
            className="flex-shrink-0 w-72 rounded-2xl border border-slate-200 bg-slate-50 p-3"
          >
            <div className="flex items-center justify-between mb-2">
              <input
                value={stage.name || ""}
                onChange={(e) => mutate((d) => { d.stages[si].name = e.target.value; })}
                disabled={!editable}
                data-testid={`designer-stage-name-${stage.id}`}
                className="bg-transparent text-sm font-bold text-slate-900 px-1 py-0.5 rounded border border-transparent focus:bg-white focus:border-slate-300 outline-none flex-1 min-w-0"
              />
              {editable && (
                <button
                  type="button"
                  className="text-slate-400 hover:text-rose-500 ml-1"
                  onClick={() => mutate((d) => { d.stages.splice(si, 1); })}
                  title="Remove stage"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="space-y-2">
              {(stage.steps || []).map((step, stepi) => (
                <div
                  key={step.id}
                  data-testid={`designer-step-${step.id}`}
                  className="rounded-lg border border-slate-300 bg-white p-2 cursor-pointer hover:border-blue-400"
                  onClick={() => editable && setEditing({ stageIndex: si, stepIndex: stepi })}
                >
                  <div className="flex items-center justify-between gap-1">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold text-slate-900 truncate">{step.name || step.key}</div>
                      <div className="text-[10px] font-mono text-slate-500 truncate">{step.key}</div>
                    </div>
                    {step.expected_days != null && (
                      <span className="text-[10px] text-slate-500 font-mono">~{step.expected_days}d</span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(step.triggers || []).map((t) => (
                      <span key={t.id} className="text-[9px] bg-indigo-50 text-indigo-700 rounded px-1.5 py-0.5 font-mono">
                        ⚡ {t.event_type}
                      </span>
                    ))}
                    {(step.actions || []).map((a) => (
                      <span key={a.id} className="text-[9px] bg-blue-50 text-blue-700 rounded px-1.5 py-0.5 font-mono">
                        {a.type}
                      </span>
                    ))}
                    {(step.conditions || []).length > 0 && (
                      <span className="text-[9px] bg-amber-50 text-amber-700 rounded px-1.5 py-0.5 font-mono flex items-center gap-0.5">
                        <GitBranch className="w-2 h-2" /> {step.conditions.length}
                      </span>
                    )}
                  </div>
                  {editable && (
                    <div className="mt-1 flex justify-end gap-0.5">
                      <button
                        type="button"
                        disabled={stepi === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          mutate((d) => {
                            const arr = d.stages[si].steps;
                            [arr[stepi - 1], arr[stepi]] = [arr[stepi], arr[stepi - 1]];
                          });
                        }}
                        className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        disabled={stepi >= stage.steps.length - 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          mutate((d) => {
                            const arr = d.stages[si].steps;
                            [arr[stepi + 1], arr[stepi]] = [arr[stepi], arr[stepi + 1]];
                          });
                        }}
                        className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          mutate((d) => { d.stages[si].steps.splice(stepi, 1); });
                        }}
                        className="text-slate-400 hover:text-rose-500"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {editable && (
                <button
                  type="button"
                  onClick={() => mutate((d) => {
                    d.stages[si].steps.push({
                      id: newId("stp_"),
                      key: `step_${(stage.steps || []).length + 1}`,
                      name: "New step",
                      order: (stage.steps || []).length + 1,
                      triggers: [], actions: [], conditions: [], outcomes: [],
                    });
                  })}
                  data-testid={`designer-add-step-${stage.id}`}
                  className="w-full text-xs text-slate-600 hover:text-slate-900 border border-dashed border-slate-300 rounded-lg py-1.5 hover:bg-white"
                >
                  + Add step
                </button>
              )}
            </div>
          </div>
        ))}
        {editable && (
          <button
            type="button"
            onClick={() => mutate((d) => {
              d.stages.push({
                id: newId("stg_"),
                key: `stage_${(d.stages || []).length + 1}`,
                name: "New stage",
                order: (d.stages || []).length + 1,
                steps: [],
              });
            })}
            data-testid="designer-add-stage"
            className="flex-shrink-0 w-72 rounded-2xl border-2 border-dashed border-slate-300 bg-white/50 p-6 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:border-slate-400"
          >
            + Add stage
          </button>
        )}
      </div>

      {editing && editingStep && (
        <StepDrawer
          step={editingStep}
          allStepKeys={allStepKeys.filter((k) => k !== editingStep.key)}
          vendorServices={vendorServices}
          eventTypes={eventTypes}
          onChange={(updated) => mutate((d) => {
            d.stages[editing.stageIndex].steps[editing.stepIndex] = updated;
          })}
          onClose={() => setEditing(null)}
        />
      )}
      <SandboxLaunchModal
        open={sandboxOpen}
        onClose={() => setSandboxOpen(false)}
        definition={defn}
      />
    </ToolPageShell>
  );
}
